import { useEffect } from "react";
import Footer from "@/components/sections/Footer";

// Terms & Conditions — canonical hosted page for FolioStax (opened by the Android app and linked
// from the site). Mirrors the content the backend used to serve at /api/v1/legal/terms; keep the
// version / last-updated in sync with the app's acceptance tracking when the copy changes.
const TERMS_VERSION = "1.0";
const TERMS_LAST_UPDATED = "2 September 2026";

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    body:
      'By creating an account or using the FolioStax application (the "App"), you agree to be bound ' +
      "by these Terms and Conditions. If you do not agree, do not use the App.",
  },
  {
    title: "2. Licence to Use",
    body:
      "FolioStax grants you a limited, non-exclusive, non-transferable, revocable licence to use the " +
      "App for managing your own business accounting and inventory. You may not resell, sublicense, " +
      "or redistribute the App or its content.",
  },
  {
    title: "3. Accounts and Security",
    body:
      "You are responsible for maintaining the confidentiality of your account credentials and for all " +
      "activity that occurs under your account. You must provide accurate registration information and " +
      "keep it up to date. Notify us immediately of any unauthorised use.",
  },
  {
    title: "4. Subscriptions and Payments",
    body:
      "Certain features require an active subscription. Fees, billing cycles, and applicable taxes are " +
      "shown at the time of purchase. Unless required by law, payments are non-refundable. We may change " +
      "pricing with prior notice; continued use after a change constitutes acceptance.",
  },
  {
    title: "5. Your Data and Backups",
    body:
      "You retain ownership of the business data you enter into the App. We process and store it to " +
      "provide the service, including optional cloud sync and backups. You are responsible for keeping " +
      "your own copies of important records. We apply reasonable safeguards but cannot guarantee against " +
      "all loss.",
  },
  {
    title: "6. Acceptable Use",
    body:
      "You agree not to misuse the App, including attempting to access it by unauthorised means, " +
      "interfering with its operation, uploading unlawful content, or using it to violate any applicable " +
      "tax, accounting, or data-protection law.",
  },
  {
    title: "7. Intellectual Property",
    body:
      "The App, its design, logos, and software are owned by FolioStax and its licensors and are " +
      "protected by intellectual-property laws. These Terms do not grant you any rights to our " +
      "trademarks or branding.",
  },
  {
    title: "8. Disclaimer of Warranties",
    body:
      'The App is provided "as is" and "as available" without warranties of any kind. FolioStax is an ' +
      "accounting and inventory tool and does not provide professional tax, legal, or financial advice. " +
      "Verify computed figures before relying on them for filings.",
  },
  {
    title: "9. Limitation of Liability",
    body:
      "To the maximum extent permitted by law, FolioStax shall not be liable for any indirect, " +
      "incidental, or consequential damages, or for loss of data, profits, or business, arising from " +
      "your use of the App.",
  },
  {
    title: "10. Termination",
    body:
      "We may suspend or terminate your access if you breach these Terms. You may stop using the App at " +
      "any time. Provisions that by their nature should survive termination will remain in effect.",
  },
  {
    title: "11. Changes to These Terms",
    body:
      'We may update these Terms from time to time. The "Last updated" date reflects the latest ' +
      "revision. Continued use of the App after changes take effect constitutes acceptance of the " +
      "revised Terms.",
  },
  {
    title: "12. Contact Us",
    body: "For questions about these Terms, contact us at info@fyzeron.com.",
  },
];

export default function Terms() {
  useEffect(() => {
    const prev = document.title;
    document.title = "FolioStax — Terms & Conditions";
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Fyzeron Technologies LLP — home">
            <img src="/fyzeron-logo-dark.png" alt="Fyzeron Technologies LLP" className="h-9 w-auto" />
          </a>
          <a href="/" className="text-sm font-semibold text-slate-600 hover:text-navy transition-colors">
            ← Back to home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Legal</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-navy mt-3">
            Terms &amp; Conditions
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            FolioStax &middot; Version {TERMS_VERSION} &middot; Last updated {TERMS_LAST_UPDATED}
          </p>

          <div className="mt-10 space-y-8">
            {SECTIONS.map((s) => (
              <section key={s.title}>
                <h2 className="font-display font-bold text-xl text-slate-900">{s.title}</h2>
                <p className="mt-2 text-slate-600 leading-relaxed">
                  {s.body.includes("info@fyzeron.com") ? (
                    <>
                      For questions about these Terms, contact us at{" "}
                      <a href="mailto:info@fyzeron.com" className="text-blue-800 font-medium hover:underline">
                        info@fyzeron.com
                      </a>
                      .
                    </>
                  ) : (
                    s.body
                  )}
                </p>
              </section>
            ))}
          </div>

          <p className="mt-14 pt-8 border-t border-slate-200 text-sm text-slate-400">
            © {new Date().getFullYear()} Fyzeron Technologies LLP. All rights reserved.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
